import type { InfiniteData, Query, QueryClient } from '@tanstack/react-query';

import {
  sessionRoomSessionsQueryKey,
  type SessionRoomEntry,
} from '#features/session-room/model/roomSessions';

/**
 * Карта инвалидации: какое событие какие ключи кеша роняет – и почему именно эти.
 * ============================================================================
 *
 * Зачем она есть. У комнаты сессии – одного предмета на экране – три ключа данных:
 * `['my-sessions','room']` (её собственный список: состав, счётчик непрочитанного, превью
 * реплики), `['session-chat-context', postId, sessionId]` (состав и состояние сессии, из
 * которых ярус голоса считает «сколько нас») и `['chatMessages', chatId]` (сама лента).
 * Пока правило «событие → ключи» жило россыпью по трём файлам, каждая починка переносила
 * потерю на соседа: окно стояло с «0/1 in voice» рядом с карточкой «2/3» той же сессии, а
 * бейдж свёрнутой комнаты не гас после прочтения. Ни то ни другое не было видно в коде –
 * чтобы это увидеть, надо было держать в голове три файла сразу.
 *
 * Что здесь есть.
 * – `INVALIDATION_TABLE` – строки «события → ключи», каждый ключ с объяснением;
 * – `invalidationKeysFor` / `invalidateFor` – единственный способ уронить ключ по событию;
 * – `eventsDroppingKey` – обратный взгляд: кто роняет вот этот ключ (нужен и тесту, и
 *   человеку, который придёт разбираться, почему экран не обновился).
 *
 * Как этим пользоваться. Событие – это либо тип кадра WebSocket ровно так, как его шлёт
 * teamplay-ws, либо собственное действие пользователя (`LOCAL_EVENTS`): своё действие сокетом
 * не возвращается вовсе (бэкенд гасит кадр как SKIPPED_SELF_ACTOR), поэтому прочтение и
 * отправка обязаны ронять ключи сами – и делают это по этой же таблице, а не по-своему.
 *
 * Чего здесь нет. Побочных действий кадра – тостов, звука, сторов, навигации: они остались в
 * обработчике сокета. Здесь только кеш.
 */

export type WsQueryKey = readonly unknown[];

/** Данные события, от которых зависят ключи. Всё необязательное: у кадра может не быть ничего. */
export type InvalidationContext = {
  /** `parentId` кадра: у сессионных событий это заявка, у чатовых – переписка. */
  parentId?: string | null;
  /** `targetId` кадра: у голосовых событий это сессия. */
  targetId?: string | null;
  /** Кадр о СВОЁМ сообщении: лента отправителя уже верна, перезапрос её только дёргает. */
  isOwnMessage?: boolean;
  /** Переписка принадлежит сессии, которую держит комната (см. `isSessionRoomChat`). */
  isRoomChat?: boolean;
  /**
   * Заявка кадра – та самая, чью сессию комната этой вкладки уже держит в кеше
   * (см. `isSessionRoomPost`). Признак заполняет сам `invalidateFor`: ответ на него лежит
   * только в кеше, а `queryClient` есть ровно там.
   */
  isRoomPost?: boolean;
  /** Первое сообщение в только что созданной переписке: ленты ещё нет, её надо забрать. */
  isNewChat?: boolean;
};

/**
 * Один ключ в строке таблицы.
 *
 * `prefix` – постоянная часть, по ней ключ и виден в обратной таблице. `tail` – идентификатор
 * из события; ключ с объявленным `tail`, для которого идентификатора нет, не роняется вовсе
 * (иначе точечный ключ превратился бы в целую семью и утащил за собой чужие экраны).
 */
type KeySpec = {
  prefix: WsQueryKey;
  tail?: (ctx: InvalidationContext) => string | null | undefined;
  /** Ключ роняется не всегда – условие здесь, рядом с ключом, а не в обработчике. */
  when?: (ctx: InvalidationContext) => boolean;
  /**
   * Без идентификатора ронять семью целиком, а не пропускать строку. Только для ключей, где
   * пропуск оставит экран стоять с неверными данными.
   */
  familyWhenNoTail?: boolean;
  /**
   * Ронять только те запросы семьи, в данных которых встречается этот идентификатор. Для
   * широковещательных кадров: изменение чужой сессии не должно перечитывать ленту у всех, кто
   * онлайн, – только у тех, у кого эта заявка на экране. Идентификатора нет – роняется семья.
   */
  mentions?: (ctx: InvalidationContext) => string | null | undefined;
  /**
   * Ключ - лента по курсору, и вместо полного перезапроса достаточно перечитать ту её страницу,
   * где лежит `mentions`-заявка. Бесконечный запрос перечитывает страницы по очереди: на трёхстах
   * заявках это тринадцать запросов подряд ради одной карточки. Страницу по курсору можно перечитать
   * одну, потому что её границы не плывут (она задана тем, "после какой заявки" её просили).
   * Только для лент по курсору: у ленты по смещению границы между запросами сдвигаются.
   * Действует, только когда `mentions` дал идентификатор; иначе роняется семья, как раньше.
   */
  pageRefresh?: boolean;
  /** Почему этот ключ в этой строке. */
  why: string;
};

export type InvalidationRow = {
  /** События, которые роняют эти ключи. */
  events: readonly string[];
  /** Что случилось, одной фразой. */
  what: string;
  keys: readonly KeySpec[];
};

/** Собственные действия: своего кадра сокета у них нет, ключи роняют сами. */
export const LOCAL_EVENTS = {
  sessionExtended: 'local:session-extended',
  /** Отметка о прочтении ушла на сервер (`markChatRead`). */
  chatRead: 'local:chat-read',
  /** Сервер подтвердил своё отправленное сообщение. */
  chatMessageSent: 'local:chat-message-sent',
  /**
   * Кадр создал уведомление (`hasNotification`). Это единственная строка не про тип кадра, а
   * про его признак: колокольчик обновляют события двух десятков типов, перечислять их здесь
   * значило бы держать копию серверного реестра правил.
   */
  notificationCreated: 'ws-flag:notification-created',
  /**
   * Автор добавил месту в сборе («Ещё место»).
   *
   * Своего кадра у этого нет вовсе - сервер на изменение размера состава не публикует ничего
   * (`SessionCompositeService.increaseTeamLimit`), и узнать о нём остальные могут только
   * следующим опросом. Поэтому свою же вкладку чиним сами, тем же набором ключей, что и приход
   * человека: дробь мест живёт в тех же местах, что и состав, - на карточке, в пилюле комнаты,
   * в ярусе голоса, в строке переписки и во всех витринах.
   */
  sessionSeatsChanged: 'local:session-seats-changed',
  /**
   * Вкладку или приложение вернули на передний план. Сокет мог всё это время молчать, будучи
   * формально открытым, - тогда кадров о новых сообщениях не было, и бейджи стоят с тем, что
   * было до ухода. Своего кадра у этого нет, ключи роняет `installForegroundRefresh`.
   */
  foreground: 'local:foreground',
} as const;

const parentIdOf = (ctx: InvalidationContext) => ctx.parentId;
const targetIdOf = (ctx: InvalidationContext) => ctx.targetId;

/** Ключ комнаты сессии – её собственный список: состав, счётчик непрочитанного, превью. */
const roomKey = (why: string): KeySpec => ({ prefix: sessionRoomSessionsQueryKey, why });

/**
 * Тот же ключ, но только когда переписка и вправду принадлежит сессии комнаты.
 *
 * Событие о прочтении или удалении приходит одним типом и для личного диалога, и для переписки
 * сессии – различить их можно только по самому чату (`isSessionRoomChat`). Без этой проверки
 * список сессий перезапрашивался бы на каждое движение в любом диалоге.
 */
const roomKeyForSessionChat = (why: string): KeySpec => ({
  ...roomKey(why),
  when: (ctx) => ctx.isRoomChat === true,
});

/**
 * Состав и состояние сессии глазами яруса голоса и полосы над перепиской.
 *
 * Тот самый ключ, из-за которого развёрнутая комната держала «0/1 in voice» рядом с карточкой
 * «2/3» той же сессии: ярус читает состав отсюда, а сессионные кадры этот ключ не роняли.
 * Без идентификатора заявки роняем семью целиком – ярус существует ради ответа на «я один?»,
 * и оставить его с неверным ответом хуже, чем перечитать пару мелких запросов.
 */
const sessionContextKey = (why: string): KeySpec => ({
  prefix: ['session-chat-context'],
  tail: parentIdOf,
  familyWhenNoTail: true,
  why,
});

/** Списки, которые показывают сессию: страница заявки, Discover, лента, профиль. */
const SESSION_LIST_KEYS: readonly KeySpec[] = [
  {
    prefix: ['my-sessions'],
    why: 'Семья «мои сессии» целиком – внутри неё лежит и список комнаты `["my-sessions","room"]`.',
  },
  { prefix: ['archived-sessions'], why: 'Завершённая сессия переезжает в архив.' },
  { prefix: ['sessions'], why: 'Списки сессий на странице заявки.' },
  { prefix: ['discover-sessions'], why: 'Витрина сессий показывает «N/M в составе».' },
  {
    prefix: ['discover-listings'],
    mentions: parentIdOf,
    pageRefresh: true,
    why: 'Витрина заявок показывает ту же сессию карточкой: перечитывается страница с этой заявкой.',
  },
  { prefix: ['discover-favourites'], why: 'Та же карточка в избранном.' },
  { prefix: ['profile-user-sessions'], why: 'Сессии в профиле участника.' },
  { prefix: ['sessions-count'], why: 'Счётчик в меню.' },
  { prefix: ['posts'], why: 'Ленты заявок несут карточку сессии.' },
  { prefix: ['feed'], why: 'Главная лента несёт её же.' },
];

/** Что показывает саму заявку: её карточка, её сессии и все витрины, где она лежит строкой. */
const POST_KEYS: readonly KeySpec[] = [
  { prefix: ['post'], tail: parentIdOf, why: 'Сама заявка.' },
  { prefix: ['post-sessions'], tail: parentIdOf, why: 'Её сессии.' },
  {
    prefix: ['post-session-slots'],
    tail: parentIdOf,
    why: 'Занятые часы заявки в окне создания сессии.',
  },
  { prefix: ['posts'], why: 'Ленты заявок.' },
  { prefix: ['discover-listings'], pageRefresh: true, why: 'Витрина заявок.' },
  { prefix: ['discover-sessions'], why: 'Витрина сессий.' },
  { prefix: ['discover-favourites'], why: 'Избранное.' },
  { prefix: ['my-post-game-ids'], why: 'Набор игр, по которым у человека есть заявки.' },
  { prefix: ['feed'], why: 'Главная лента.' },
];

/**
 * Те же ключи для широковещательных кадров об уже существующей заявке (POST_UPDATED,
 * POST_DELETED). Кадр приходит каждому, кто онлайн, на любую правку любой чужой заявки, и раньше
 * перечитывал ленту и витрины у всех, у кого они на экране. Теперь списки роняются только там,
 * где эта заявка лежит в данных; набор своих игр – как раньше, в нём id заявок нет вовсе.
 */
const POST_KEYS_MENTIONING_POST: readonly KeySpec[] = POST_KEYS.map((spec) =>
  spec.tail || spec.prefix[0] === 'my-post-game-ids' ? spec : { ...spec, mentions: parentIdOf }
);

/** Ключи, которые роняет любое движение в переписке: список слева и счётчики непрочитанного. */
const CHAT_TRAFFIC_KEYS: readonly KeySpec[] = [
  { prefix: ['chats'], why: 'Превью последней реплики, время и бейдж в списке переписок.' },
  { prefix: ['chatsUnreadExist'], why: 'Точка «есть непрочитанное» в меню.' },
  { prefix: ['chatsUnreadCount'], why: 'Число непрочитанного в меню.' },
  {
    prefix: ['chat-counts'],
    // Числа на вкладках спрашиваются отдельным запросом от самой страницы списка (список
    // приезжает по двадцать строк, а вкладки обязаны знать про все переписки). Раз запрос
    // отдельный, то и уронить его должно движение в переписке – иначе «Личное 3» стоит
    // до перезагрузки страницы, пока список под ним уже обновился.
    why: 'Числа на вкладках «Все / Личное / Сессии» и непрочитанное в них.',
  },
];

const SESSION_CHANGE_KEYS: readonly KeySpec[] = [
  { prefix: ['post'], tail: parentIdOf, why: 'Карточка заявки с составом сессии.' },
  { prefix: ['post-sessions'], tail: parentIdOf, why: 'Список сессий этой заявки.' },
  { prefix: ['discordRoom'], tail: parentIdOf, why: 'Комната Discord знает состав.' },
  {
    prefix: ['invite-link'],
    why: 'Состав изменился – значит изменился ответ на «могу ли я занять место по ссылке».',
  },
  sessionContextKey(
    'Ярус голоса и полоса над перепиской читают состав сессии отсюда: вход и выход ' +
      'участника – ровно то событие, ради которого они существуют.'
  ),
  {
    prefix: ['chats'],
    why:
      'Строка переписки сессии рисует лица и число участников из состава ЧАТА: вступивший ' +
      'попадает в переписку вместе с сессией, и без этого счётчик врал до перезагрузки.',
  },
  {
    prefix: ['chat-peer-sessions'],
    why: 'Актуальная сессия собеседника в шапке личного чата.',
  },
  {
    prefix: ['friends-invitable-sessions'],
    why: 'Меню приглашения учитывает актуальный состав.',
  },
  ...SESSION_LIST_KEYS,
];

export const INVALIDATION_TABLE: readonly InvalidationRow[] = [
  {
    events: [
      LOCAL_EVENTS.sessionExtended,
      // SESSION_UPDATED сервер больше не рассылает (правило снято миграцией 62 notification), строка
      // оставлена ради старых вкладок, пока они живы.
      'SESSION_UPDATED',
      'SESSION_CREATED',
      'SESSION_MEMBER_JOINED',
      'SESSION_MEMBER_LEFT',
      'SESSION_MEMBER_REMOVED',
      /*
        Вход по ссылке — тоже изменение состава, а не только повод порадовать пригласившего.

        Общий кадр `SESSION_MEMBER_JOINED` тому, кто выдал ссылку, сервер намеренно гасит, чтобы
        не слать ему два сообщения об одном событии, — и присылает вместо него это. Пока его
        здесь не было, у хозяина заявки, поделившегося собственной ссылкой, карточка не менялась
        вовсе: «нужен ещё 1 игрок» стояло на месте до перезагрузки, хотя человек уже вошёл.
      */
      'SESSION_INVITE_LINK_REDEEMED',
      'SESSION_COMPLETED',
      'SESSION_DISCORD_ACTIVATED',
      'DISCORD_VOICE_MEMBERS_CHANGED',
    ],
    what: 'Состав, время или состояние сессии изменились.',
    keys: SESSION_CHANGE_KEYS,
  },
  {
    events: ['SESSION_VOICE_MODE_CHANGED'],
    what: 'Автор включил голос Teamplay.',
    keys: [
      ...SESSION_CHANGE_KEYS,
      {
        prefix: ['voiceRoom'],
        tail: targetIdOf,
        why: 'Перечитать снимок, который был недоступен в NONE/IN_GAME.',
      },
    ],
  },
  {
    events: [
      'SESSION_VOICE_STARTED',
      'VOICE_PARTICIPANT_JOINED',
      'VOICE_PARTICIPANT_LEFT',
      'VOICE_PARTICIPANT_REMOVED',
      'VOICE_ROOM_CLOSED',
    ],
    what: 'Кто-то вошёл в голос или вышел из него.',
    keys: [
      { prefix: ['voiceRoom'], tail: targetIdOf, why: 'Серверный снимок голосовой комнаты.' },
      { prefix: ['post'], tail: parentIdOf, why: 'Счётчик «N в голосе» на карточке заявки.' },
      { prefix: ['post-sessions'], tail: parentIdOf, why: 'Тот же счётчик в списке сессий.' },
      {
        prefix: ['my-sessions'],
        why: 'Семья целиком: счётчик голоса в комнате живёт полем `voiceRoom` её списка.',
      },
      { prefix: ['sessions'], why: 'Списки сессий на странице заявки.' },
      { prefix: ['discover-sessions'], why: 'Витрина сессий показывает «N в голосе».' },
      { prefix: ['posts'], why: 'Ленты заявок несут тот же счётчик.' },
      { prefix: ['feed'], why: 'Главная лента несёт его же.' },
      // Ключ состава здесь намеренно НЕ роняется: `session-chat-context` не знает о голосе
      // ничего – ни счётчика, ни поимённого состава. Голос ярус берёт из `voiceRoom`.
    ],
  },
  {
    events: [
      'SESSION_MEMBER_INVITED',
      'SESSION_INVITE_DECLINED',
      'SESSION_INVITE_REVOKED',
      'SESSION_CANCEL',
      'SESSION_REMINDER',
    ],
    what: 'Приглашение или судьба сессии изменились.',
    keys: [
      {
        prefix: ['chat-peer-sessions'],
        why: 'Отмена и приглашения меняют статус собеседника в чате.',
      },
      {
        prefix: ['friends-invitable-sessions'],
        why: 'Меню приглашения учитывает актуальный состав.',
      },
      { prefix: ['post'], tail: parentIdOf, why: 'Карточка заявки.' },
      { prefix: ['post-sessions'], tail: parentIdOf, why: 'Список сессий этой заявки.' },
      sessionContextKey(
        'Ярус и полоса показывают и статус сессии, и её участников со статусами: отменённая ' +
          'сессия обязана перестать выглядеть идущей в тот же миг, а не после перезагрузки.'
      ),
      { prefix: ['my-sessions'], why: 'Семья целиком: приглашение меняет и список комнаты.' },
      { prefix: ['sessions'], why: 'Списки сессий на странице заявки.' },
    ],
  },
  {
    events: ['POST_BUMP_REMINDER'],
    what: 'Заявке пора вверх.',
    keys: [{ prefix: ['post'], tail: parentIdOf, why: 'Кнопка поднятия живёт на карточке.' }],
  },
  {
    events: ['POST_CREATED'],
    what: 'Заявка появилась.',
    /*
      Витрину заявок НЕ перечитываем: новая заявка встала бы сверху под читателем, который
      прокрутил ленту, и плашка «N новых заявок» не появилась бы никогда: она считает «новее
      верхней карточки», а верхней уже была бы новая. Вместо перечитывания будим счёт, и плашка
      зажигается сразу, а не через минуту опроса. Остальные витрины роняются целиком, как раньше.
    */
    keys: [
      ...POST_KEYS.filter((key) => key.prefix[0] !== 'discover-listings'),
      {
        prefix: ['discover-new-count'],
        why: 'Плашка «N новых заявок» считает те, что новее верхней карточки ленты.',
      },
    ],
  },
  {
    events: ['POST_DELETED'],
    what: 'Заявка исчезла.',
    keys: POST_KEYS_MENTIONING_POST,
  },
  {
    events: ['POST_UPDATED'],
    what: 'Заявка изменилась – в том числе тем, что её сессию завершили не в этой вкладке.',
    keys: [
      ...POST_KEYS_MENTIONING_POST,
      {
        prefix: ['my-sessions'],
        // Хозяин завершает сессию из соседней вкладки, с телефона или вызовом по API: своего
        // SESSION_COMPLETED эта вкладка не получит (бэкенд гасит кадр как SKIPPED_SELF_ACTOR),
        // а POST_UPDATED приходит широковещательно всем – это единственный сигнал, который
        // здесь есть. Без него пилюля комнаты стоит «LIVE | 60 мин» над сессией, которую
        // сервер уже считает завершённой.
        //
        // Условие обязательно. POST_UPDATED летит каждому онлайн-клиенту на любое движение в
        // любой чужой заявке; ронять на него семью «мои сессии» без разбора значило бы, что
        // весь сайт перечитывает свои сессии на каждую правку чужого объявления. Спрашиваем
        // уже загруженный кеш комнаты: заявки нет в нём – ронять нечего по определению.
        when: (ctx) => ctx.isRoomPost === true,
        why: 'Семья целиком: комната читает состояние сессии из `["my-sessions","room"]`, а те же данные несут списки сессий на странице заявки.',
      },
    ],
  },
  {
    events: ['COMMENT_CREATED', 'COMMENT_REPLY'],
    what: 'Комментарий под заявкой.',
    keys: [
      { prefix: ['post'], tail: parentIdOf, why: 'Счётчик комментариев на карточке.' },
      { prefix: ['comments'], why: 'Сама лента комментариев.' },
    ],
  },
  {
    events: ['CHAT_MESSAGE'],
    what: 'Сообщение в личной переписке.',
    keys: [
      ...CHAT_TRAFFIC_KEYS,
      { prefix: ['notifications'], why: 'Сообщение попадает в колокольчик.' },
      {
        prefix: ['chatMessages'],
        tail: parentIdOf,
        // Своё же сообщение ленту НЕ перезапрашивает: отправка уже вписала его на своё место,
        // а перезапрос подменял список целиком – и на стыке двух состояний лента дёргалась.
        when: (ctx) => !ctx.isOwnMessage,
        why: 'У собеседника этого сообщения нет вовсе, взять его больше неоткуда.',
      },
      // Ключ комнаты здесь не роняется: личная переписка комнате никак не приходится, иначе
      // список сессий перезапрашивался бы на каждое сообщение в любом диалоге.
    ],
  },
  {
    events: ['SESSION_CHAT_MESSAGE'],
    what: 'Сообщение в переписке сессии.',
    keys: [
      ...CHAT_TRAFFIC_KEYS,
      { prefix: ['notifications'], why: 'Сообщение попадает в колокольчик.' },
      roomKey(
        'Свёрнутая комната читает превью и счётчик не из `["chats"]`, а из своего списка: ' +
          '`lastMessageText` и `unreadCount` приезжают полем `sessionChat` внутри сессии. ' +
          'Ключ точечный, а не вся семья: реплика не меняет ни состава, ни состояния сессии.'
      ),
      {
        prefix: ['chatMessages'],
        tail: parentIdOf,
        when: (ctx) => !ctx.isOwnMessage,
        why: 'То же, что и в личной переписке: у остальных участников сообщения ещё нет.',
      },
    ],
  },
  {
    events: ['SESSION_CHAT_SYSTEM_MESSAGE'],
    what:
      'Служебная строка в переписке сессии: сессия началась или закончилась, кто-то вступил, ' +
      'вышел или зашёл в голос.',
    keys: [
      {
        prefix: ['chatMessages'],
        tail: parentIdOf,
        why:
          'Открытый тред узнаёт о строке только так: запись в базу клиенту ни о чём не сообщала, ' +
          'и «сессия закончилась» появлялась лишь после перезагрузки (замер на телефоне 14.09.2026).',
      },
      {
        prefix: ['chats'],
        why: 'Превью строки переписки – последнее событие. Счётчиков строка не меняет: у неё нет автора.',
      },
      roomKey('Превью свёрнутой комнаты читает `lastMessageText` из своего списка.'),
      {
        prefix: ['session-chat-context'],
        // В кадре id переписки, а ключ собран из заявки и сессии: точечно не попасть, роняем семью.
        // Строка приходит на старт, конец, вступление и выход - то есть ровно на смену состояния.
        why:
          'Шапка треда и полоса над лентой берут статус и состав отсюда. Завершившему сессию ' +
          'клиенту `SESSION_COMPLETED` не приходит, и без этого шапка стояла «Live now» с ' +
          '«Join voice» до перезагрузки (смоук ROOM-11).',
      },
    ],
  },
  {
    events: [
      'CHAT_READ',
      // Legacy alias – убрать, когда переименование доедет до всех сервисов.
      'CHAT_MESSAGES_READ',
    ],
    what: 'Переписку прочитали (на этом устройстве или на соседнем).',
    keys: [
      ...CHAT_TRAFFIC_KEYS,
      roomKeyForSessionChat(
        'Бейдж свёрнутой комнаты – это `unreadCount` её списка, и он гаснет только так. ' +
          'Только для переписки сессии: чужой диалог кеш комнаты трогать не обязан.'
      ),
      {
        prefix: ['chatMessages'],
        tail: parentIdOf,
        why: 'Галочки прочтения стоят у сообщений ленты.',
      },
    ],
  },
  {
    events: ['CHAT_MESSAGE_DELETED'],
    what: 'Собеседник убрал своё сообщение.',
    keys: [
      ...CHAT_TRAFFIC_KEYS,
      {
        prefix: ['chatMessages'],
        tail: parentIdOf,
        why: 'На месте текста должно встать «Сообщение удалено».',
      },
      // Список переписок слева роняется общими ключами выше — и это здесь важно, а не побочно:
      // удалённое сообщение могло быть последним, и превью осталось бы с текстом, которого
      // больше нет. Реакции, для сравнения, превью не трогают.
    ],
  },
  {
    events: ['CHAT_REACTION', 'CHAT_MESSAGE_REACTION'],
    what: 'Реакция на сообщение.',
    keys: [
      ...CHAT_TRAFFIC_KEYS,
      {
        prefix: ['chatMessages'],
        tail: parentIdOf,
        why: 'Счётчики реакций стоят у пузыря.',
      },
      // Ключ комнаты не роняется: реакция не меняет ни превью последней реплики, ни числа
      // непрочитанного – единственного, что комната берёт из своего списка про переписку.
    ],
  },
  {
    /*
      Я ушёл из переписки на другом устройстве.

      Ключи те же, что у удаления, и по той же причине: переписка уходит из МОЕГО списка вместе
      со своим непрочитанным, а число на вкладке «Сессии» становится на одну меньше. Ленту и
      комнату не трогаем: у остальных разговор жив, и переписка вернётся, стоит мне вступить в
      сессию заново.
    */
    events: ['CHAT_LEFT'],
    what: 'Я вышел из переписки сессии - на этом устройстве тоже.',
    keys: [
      { prefix: ['chats'], why: 'Переписка уходит из моего списка слева.' },
      { prefix: ['chat-counts'], why: 'Переписок стало на одну меньше - это число на вкладке.' },
      { prefix: ['chatsUnreadExist'], why: 'Точка «есть непрочитанное» в меню.' },
      { prefix: ['chatsUnreadCount'], why: 'Число непрочитанного в меню.' },
    ],
  },
  {
    events: ['CHAT_DELETED'],
    what: 'Переписку удалили.',
    keys: [
      { prefix: ['chats'], why: 'Переписка уходит из списка слева.' },
      {
        prefix: ['chat-counts'],
        // Здесь ключ стоит отдельной строкой, а не приезжает из CHAT_TRAFFIC_KEYS: удаление
        // не трогает превью и бейджи, ради которых заведена та тройка, зато меняет ровно то
        // число, которое считает переписки.
        why: 'Переписок стало на одну меньше – это и есть число на вкладке.',
      },
      /*
        Непрочитанное удалённой переписки уходит вместе с ней. Без этих двух ключей точка и число
        в меню держали сообщения, которых больше нет нигде, – до перезагрузки или до следующего
        сообщения в любой другой переписке.
      */
      { prefix: ['chatsUnreadExist'], why: 'Точка «есть непрочитанное» в меню.' },
      { prefix: ['chatsUnreadCount'], why: 'Число непрочитанного в меню.' },
      roomKeyForSessionChat(
        'У сессии больше нет чата: комната обязана перестать обещать переписку по мёртвому id. ' +
          'Только для переписки сессии – личная комнате не приходится никак.'
      ),
      { prefix: ['chatMessages'], tail: parentIdOf, why: 'Открытая лента больше не существует.' },
    ],
  },
  {
    events: ['CALL_MISSED', 'CALL_REJECTED', 'CALL_CANCELLED', 'CALL_ENDED'],
    what: 'Звонок кончился – в переписке появилась системная карточка.',
    keys: [
      {
        prefix: ['chatMessages'],
        // Семья целиком, а не переписка звонка: у терминальных кадров звонка идентификатор
        // переписки задан не всегда, а карточка обязана появиться. Это известная лишняя
        // работа – сузить её можно, когда кадры начнут нести `parentId` всегда.
        why: 'Карточка звонка ложится в ленту как обычное системное сообщение.',
      },
      { prefix: ['chats'], why: 'Она же становится превью в списке переписок.' },
    ],
  },
  {
    events: ['USER_FOLLOWED', 'USER_FOLLOW_MUTUAL', 'USER_UNFOLLOWED'],
    what: 'Связь между людьми изменил кто-то другой – пересчитать её локально нечем.',
    keys: [
      { prefix: ['follow-list'], why: 'Подписавшийся переезжает между секциями экрана связей.' },
      { prefix: ['follow-counts'], why: 'Бейдж на пункте меню считается отдельным запросом.' },
      { prefix: ['follow'], why: 'Кнопка-статус и счётчик на открытом профиле.' },
      {
        prefix: ['call-reach'],
        why: 'Допуск звонков считается от дружбы: подписался в ответ - трубка в шапке переписки.',
      },
    ],
  },
  {
    events: ['NOTIFICATION_READ'],
    what: 'Уведомление прочитано.',
    keys: [{ prefix: ['notifications'], why: 'Список уведомлений.' }],
  },
  {
    events: [LOCAL_EVENTS.notificationCreated],
    what: 'Кадр создал уведомление (признак `hasNotification`).',
    keys: [
      { prefix: ['notifications'], why: 'Список уведомлений под колокольчиком.' },
      { prefix: ['hasUnreadNotifications'], why: 'Точка на самом колокольчике.' },
    ],
  },
  {
    events: [LOCAL_EVENTS.chatRead],
    what: 'Я прочитал переписку. Своего кадра сокета у этого нет (SKIPPED_SELF_ACTOR).',
    keys: [
      ...CHAT_TRAFFIC_KEYS,
      roomKeyForSessionChat(
        'Тот же бейдж комнаты, что и у кадра CHAT_READ, только гасит его собственное ' +
          'прочтение: открыл, прочитал, свернул – бейдж обязан погаснуть сразу.'
      ),
      // Лента здесь не роняется намеренно: прочитанное гасится в кеше на месте
      // (`applyLocalRead`), а перезапрос сбил бы и позицию прокрутки, и границу непрочитанного.
    ],
  },
  {
    events: [LOCAL_EVENTS.chatMessageSent],
    what: 'Сервер подтвердил моё сообщение. Своего кадра сокета у этого нет.',
    keys: [
      ...CHAT_TRAFFIC_KEYS,
      roomKeyForSessionChat(
        'Превью свёрнутой комнаты обязано показать мою же реплику: кадр SESSION_CHAT_MESSAGE ' +
          'до отправителя не доходит, и обновить превью больше нечем.'
      ),
      {
        prefix: ['chatMessages'],
        tail: parentIdOf,
        // Только у первого сообщения новой переписки: ленты ещё не существует, оптимистичной
        // строки тоже, вписывать нечего. В остальных случаях список уже верен.
        when: (ctx) => ctx.isNewChat === true,
        why: 'Первое сообщение в только что созданной переписке – страницу забираем запросом.',
      },
    ],
  },
  {
    events: [LOCAL_EVENTS.sessionSeatsChanged],
    what: 'Я добавил место в своём сборе. Своего кадра сокета у этого нет вовсе.',
    keys: [
      { prefix: ['post'], tail: parentIdOf, why: 'Карточка заявки с дробью мест.' },
      { prefix: ['post-sessions'], tail: parentIdOf, why: 'Список сессий этой заявки.' },
      { prefix: ['invite-link'], why: 'Ответ на «могу ли я занять место по ссылке» изменился.' },
      sessionContextKey('Ярус голоса и полоса над перепиской считают места из состава сессии.'),
      {
        prefix: ['chats'],
        why: 'Строка переписки сессии показывает число участников и свободные места.',
      },
      {
        prefix: ['chat-peer-sessions'],
        why: 'Актуальная сессия собеседника в шапке личного чата.',
      },
      {
        prefix: ['friends-invitable-sessions'],
        why: 'Меню приглашения учитывает актуальный состав.',
      },
      ...SESSION_LIST_KEYS,
    ],
  },
  {
    events: [LOCAL_EVENTS.foreground],
    what:
      'Вкладку или приложение вернули на передний план. Страховка на случай молчащего сокета: ' +
      'без неё бейдж Chats догонял сервер только после перезагрузки страницы.',
    keys: [
      // Список и числа на вкладках - вместе со счётчиком в меню, иначе бейдж в меню уже
      // «3», а строка переписки в открытом списке ещё без непрочитанного.
      ...CHAT_TRAFFIC_KEYS,
      { prefix: ['hasUnreadNotifications'], why: 'Точка на самом колокольчике.' },
      { prefix: ['notifications'], why: 'Список под колокольчиком, если он открыт.' },
      /*
        Сама открытая переписка — тоже страховка, и именно её здесь не хватало.

        Список чатов и счётчики догоняли сервер при возврате на экран, а лента открытого
        разговора оставалась на том, что успела получить: потерянный по дороге кадр дорисовать
        было нечем, кроме выхода и повторного входа. Ключ без хвоста — идентификатора переписки
        в этот момент нет, а перечитывается всё равно только то, что сейчас на экране.
      */
      { prefix: ['chatMessages'], why: 'Лента открытого разговора, если кадр не доехал.' },
    ],
  },
];

const rowsByEvent = new Map<string, InvalidationRow[]>();
for (const row of INVALIDATION_TABLE) {
  for (const event of row.events) {
    rowsByEvent.set(event, [...(rowsByEvent.get(event) ?? []), row]);
  }
}

const resolveKey = (spec: KeySpec, ctx: InvalidationContext): WsQueryKey | null => {
  if (spec.when && !spec.when(ctx)) {
    return null;
  }

  if (!spec.tail) {
    return spec.prefix;
  }

  const tail = spec.tail(ctx);
  if (tail) {
    return [...spec.prefix, tail];
  }

  return spec.familyWhenNoTail ? spec.prefix : null;
};

export const isSameQueryKey = (left: WsQueryKey, right: WsQueryKey): boolean =>
  left.length === right.length && left.every((value, index) => value === right[index]);

/** Ключ запроса начинается с этого префикса – значит событие его роняет. */
export const isQueryKeyPrefix = (queryKey: WsQueryKey, prefix: WsQueryKey): boolean =>
  prefix.length <= queryKey.length && prefix.every((value, index) => queryKey[index] === value);

/**
 * Ключи, которые роняет это событие. Событие, которого нет в таблице, не роняет ничего – это
 * нормально: половина кадров сокета существует ради тоста или стора, а не ради кеша.
 */
export const invalidationKeysFor = (event: string, ctx: InvalidationContext = {}): WsQueryKey[] => {
  const keys = invalidationTargetsFor(event, ctx).map((target) => target.key);

  return keys.filter(
    (key, index) => keys.findIndex((candidate) => isSameQueryKey(candidate, key)) === index
  );
};

type InvalidationTarget = { key: WsQueryKey; mention: string | null; pageRefresh: boolean };

/** Ключи вместе с условием «только если в данных есть этот id» – для `invalidateFor`. */
const invalidationTargetsFor = (event: string, ctx: InvalidationContext): InvalidationTarget[] =>
  (rowsByEvent.get(event) ?? [])
    .flatMap((row) => row.keys)
    .flatMap((spec) => {
      const key = resolveKey(spec, ctx);
      return key
        ? [{ key, mention: spec.mentions?.(ctx) ?? null, pageRefresh: spec.pageRefresh === true }]
        : [];
    });

/**
 * Встречается ли идентификатор где-то в данных запроса. Обход с ранним выходом и потолком
 * глубины: лента в сотню карточек проверяется за доли миллисекунды и без сериализации.
 */
export const dataMentions = (data: unknown, id: string, depth = 0): boolean => {
  if (data === id) {
    return true;
  }
  if (depth > 12 || data === null || typeof data !== 'object') {
    return false;
  }
  if (Array.isArray(data)) {
    return data.some((item) => dataMentions(item, id, depth + 1));
  }
  return Object.values(data).some((value) => dataMentions(value, id, depth + 1));
};

/** Обратный взгляд: какие события роняют ключ с этим префиксом. */
export const eventsDroppingKey = (prefix: WsQueryKey): string[] =>
  INVALIDATION_TABLE.filter((row) =>
    row.keys.some((spec) => isSameQueryKey(spec.prefix, prefix))
  ).flatMap((row) => [...row.events]);

/**
 * Уронить всё, что положено этому событию, одним обращением к кешу.
 *
 * Только `invalidate`, без `cancelQueries`. Отмена била по запросу, которого в этот момент ждёт
 * лоадер маршрута: на медленном канале кадр о свежесозданном посте приходил, пока /posts/$postId
 * ещё await'ил ensureQueryData, промис лоадера режектился (CancelledError), дерево поста
 * схлопывалось и разворачивалось через Suspense – и это добивало страницу до React #185
 * (прод, 04–05.08.2026). `invalidate` сам корректно перезапустит активные запросы.
 */
export const invalidateFor = (
  queryClient: QueryClient,
  event: string,
  ctx: InvalidationContext = {}
): Promise<void> => {
  const targets = invalidationTargetsFor(event, {
    ...ctx,
    // Признак «эта заявка – та самая, чью сессию держит комната» живёт только в кеше, и
    // спросить его больше негде: в кадре сокета его нет, а обработчик кадра о комнате не знает
    // вовсе. Отвечаем здесь, где `queryClient` уже под рукой, – ценой одного чтения кеша.
    // Явно переданный признак не трогаем: тесты и вызывающий вправе сказать своё.
    isRoomPost: ctx.isRoomPost ?? isSessionRoomPost(queryClient, ctx.parentId),
  });

  if (targets.length === 0) {
    return Promise.resolve();
  }

  // Лента по курсору с известной заявкой перечитывает одну страницу, остальное роняется целиком.
  const pageTargets = targets.filter((target) => target.pageRefresh && target.mention);
  const fullTargets = targets.filter((target) => !(target.pageRefresh && target.mention));

  const full =
    fullTargets.length === 0
      ? Promise.resolve()
      : queryClient
          .invalidateQueries({
            predicate: (query) =>
              fullTargets.some(
                (target) =>
                  isQueryKeyPrefix(query.queryKey, target.key) &&
                  (!target.mention || dataMentions(query.state.data, target.mention))
              ),
          })
          .catch(() => undefined);
  const pages = Promise.all(
    pageTargets.map((target) =>
      refreshFeedPagesMentioning(queryClient, target.key, target.mention as string)
    )
  );

  return Promise.all([full, pages]).then(() => undefined);
};

type FeedPage = { records?: ({ id?: string } | null | undefined)[] };

/**
 * Свежая страница ложится на место старой, но не шире неё: остаются только карточки, которые на
 * странице уже были, в свежем порядке и со свежими данными; признаки страницы (`hasMore`,
 * `nextCursor`) остаются прежними, по ним листают дальше.
 *
 * Зачем так. Страница по курсору начинается "после такой-то заявки", и если наверху что-то
 * появилось, свежая первая страница несёт новые карточки. Вставь мы их, лента выросла бы сверху
 * над читающим и сдвинула бы ему экран. Новое показывает плашка "N новых", а здесь только
 * обновляется то, что человек уже видит. Карточка, пропавшая из свежей страницы, удалена или
 * поднялась выше: со страницы она уходит, наверху её покажет плашка.
 */
const mergeRefreshedPage = (old: unknown, fresh: unknown): unknown => {
  const oldPage = old as FeedPage;
  const freshPage = fresh as FeedPage;
  if (!Array.isArray(oldPage?.records) || !Array.isArray(freshPage?.records)) {
    return fresh;
  }
  const knownIds = new Set(oldPage.records.map((record) => record?.id));
  return {
    ...freshPage,
    ...oldPage,
    records: freshPage.records.filter((record) => record?.id && knownIds.has(record.id)),
  };
};

/**
 * Перечитать одну страницу ленты по курсору: ту, где лежит заявка, по её же курсору.
 *
 * Страница по курсору задана параметром, с которым её просили (`pageParams[i]`, у первой `null`),
 * поэтому её можно запросить заново одну и подставить на место, не трогая остальные. Если рядом
 * что-то сдвинулось, соседняя страница начнётся с уже показанной карточки, и склейка страниц по id
 * это убирает. Перечитывается и лента, которой сейчас никто не смотрит (кеш ушедшего экрана): при
 * возврате она покажется сразу, без перечитывания всех страниц.
 *
 * Заявки нет ни на одной загруженной странице - ничего не делаем: карточка, которой нет в данных,
 * обновиться не может, а новая подъедет вместе со следующим полным обновлением. Сбой запроса
 * оставляет старые данные и помечает ленту протухшей: полное перечитывание при следующем показе
 * лучше, чем карточка, которая молча осталась старой.
 */
export const refreshFeedPagesMentioning = async (
  queryClient: QueryClient,
  prefix: WsQueryKey,
  id: string
): Promise<void> => {
  const queries = queryClient.getQueryCache().findAll({ queryKey: prefix });
  await Promise.all(queries.map((query) => refreshPageMentioning(queryClient, query, id)));
};

const refreshPageMentioning = async (
  queryClient: QueryClient,
  query: Query,
  id: string
): Promise<void> => {
  const data = query.state.data as InfiniteData<unknown> | undefined;
  if (!data || !Array.isArray(data.pages)) {
    return;
  }
  const index = data.pages.findIndex((page) => dataMentions(page, id));
  if (index === -1) {
    return;
  }
  // Идёт полное перечитывание: оно и так принесёт свежую страницу, второй запрос лишний.
  if (query.state.fetchStatus === 'fetching') {
    return;
  }
  const queryFn = query.options.queryFn;
  if (typeof queryFn !== 'function') {
    return;
  }

  const pageParam = data.pageParams[index];
  try {
    const fresh = await queryFn({
      client: queryClient,
      queryKey: query.queryKey,
      signal: new AbortController().signal,
      meta: query.meta,
      pageParam,
      direction: 'forward',
    } as never);

    queryClient.setQueryData<InfiniteData<unknown>>(query.queryKey, (old) => {
      // Пока страница летела, состав ленты мог измениться (страницу сбросили или дозагрузили):
      // положенная по старому индексу, она легла бы не туда.
      if (!old || old.pageParams[index] !== pageParam) {
        return old;
      }
      return {
        ...old,
        pages: old.pages.map((page, i) => (i === index ? mergeRefreshedPage(page, fresh) : page)),
      };
    });
  } catch {
    await queryClient
      .invalidateQueries({ queryKey: query.queryKey, exact: true })
      .catch(() => undefined);
  }
};

/**
 * Эта переписка принадлежит сессии, которую держит комната?
 *
 * Спрашиваем уже загруженный список комнаты, а не сервер: комната висит в корне лейаута, её
 * список есть на любой странице, а лишний запрос ради ответа «трогать ли кеш» стоил бы дороже
 * самого обновления. Списка нет – ответ «нет»: обновлять тогда всё равно нечего.
 */
export const isSessionRoomChat = (
  queryClient: QueryClient,
  chatId: string | null | undefined
): boolean => {
  if (!chatId) {
    return false;
  }

  const sessions = queryClient.getQueryData<SessionRoomEntry[]>(sessionRoomSessionsQueryKey);

  return (sessions ?? []).some((session) => session.chatId === chatId);
};

/**
 * Эта заявка – та самая, чью сессию держит комната?
 *
 * Тот же приём, что и у `isSessionRoomChat`, и по той же причине: список комнаты уже загружен
 * на любой странице, а спрашивать сервер ради ответа «трогать ли кеш» дороже самого обновления.
 * Списка нет – ответ «нет»: обновлять тогда нечего.
 */
export const isSessionRoomPost = (
  queryClient: QueryClient,
  postId: string | null | undefined
): boolean => {
  if (!postId) {
    return false;
  }

  const sessions = queryClient.getQueryData<SessionRoomEntry[]>(sessionRoomSessionsQueryKey);

  return (sessions ?? []).some((session) => session.postId === postId);
};
